import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsAPI } from '../lib/api';

// Default logo URL - Bold version with transparent background
const DEFAULT_LOGO = 'https://static.prod-images.emergentagent.com/jobs/6e3db625-2735-4adc-add6-b5c678f43258/images/2393f304839c16b8a724880dab410701255cd39121b8f8cb2080482404962489.png';

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({
    phone_number: '+1 (787) 555-0123',
    whatsapp_number: '17875550123',
    email: 'hello@tourfinder.pr',
    address: '123 Calle del Sol, San Juan, PR 00901',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    youtube_url: '',
    tiktok_url: '',
    site_logo_url: DEFAULT_LOGO,
    // Site Colors - Customizable backgrounds
    home_bg_color: '#ffffff',
    tours_bg_color: '#f9fafb',
    hero_bg_color: '#166534',
    primary_color: '#166534',
    secondary_color: '#f59e0b',
    // Urgency & Social Proof Settings - default to true
    show_popular_badge: true,
    show_selling_fast_badge: true,
    show_spots_left: true,
    show_live_viewers: true,
    show_recent_bookings: true,
    show_book_ahead_badge: true,
    show_booking_toast: true,
    show_limited_availability: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingsAPI.get();
        if (response.data) {
          setSettings(prev => ({
            ...prev,
            ...response.data,
            site_logo_url: response.data.site_logo_url || DEFAULT_LOGO,
            // Site colors with defaults
            home_bg_color: response.data.home_bg_color || '#ffffff',
            tours_bg_color: response.data.tours_bg_color || '#f9fafb',
            hero_bg_color: response.data.hero_bg_color || '#166534',
            primary_color: response.data.primary_color || '#166534',
            secondary_color: response.data.secondary_color || '#f59e0b',
            // Ensure boolean defaults
            show_popular_badge: response.data.show_popular_badge !== false,
            show_selling_fast_badge: response.data.show_selling_fast_badge !== false,
            show_spots_left: response.data.show_spots_left !== false,
            show_live_viewers: response.data.show_live_viewers !== false,
            show_recent_bookings: response.data.show_recent_bookings !== false,
            show_book_ahead_badge: response.data.show_book_ahead_badge !== false,
            show_booking_toast: response.data.show_booking_toast !== false,
            show_limited_availability: response.data.show_limited_availability !== false,
          }));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const refreshSettings = async () => {
    try {
      const response = await settingsAPI.get();
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          site_logo_url: response.data.site_logo_url || DEFAULT_LOGO,
          // Site colors with defaults
          home_bg_color: response.data.home_bg_color || '#ffffff',
          tours_bg_color: response.data.tours_bg_color || '#f9fafb',
          hero_bg_color: response.data.hero_bg_color || '#166534',
          primary_color: response.data.primary_color || '#166534',
          secondary_color: response.data.secondary_color || '#f59e0b',
          show_popular_badge: response.data.show_popular_badge !== false,
          show_selling_fast_badge: response.data.show_selling_fast_badge !== false,
          show_spots_left: response.data.show_spots_left !== false,
          show_live_viewers: response.data.show_live_viewers !== false,
          show_recent_bookings: response.data.show_recent_bookings !== false,
          show_book_ahead_badge: response.data.show_book_ahead_badge !== false,
          show_booking_toast: response.data.show_booking_toast !== false,
          show_limited_availability: response.data.show_limited_availability !== false,
        }));
      }
    } catch (error) {
      console.error('Error refreshing settings:', error);
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
